<script setup lang="ts">
//#region imports
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import type {
  GetTeamsByIdPermissionsResponse,
  GetTeamsByIdResponse,
} from '@/types/openapi-ts';
//#endregion

//#region types
type Team = GetTeamsByIdResponse['payload'];
type TeamPermissions = GetTeamsByIdPermissionsResponse['payload'];

interface Props {
  permissions?: Partial<TeamPermissions>;
  team?: Partial<Team>;
}

interface Emits {
  'open-delete': [];
  'open-edit': [];
  'refresh': [];
  'refresh-users-list': [];
  'refresh-members': [];
  'update-permissions': [data: TeamPermissions];
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  permissions: () => ({
    affiliates_types_edit: [],
    affiliates_types_view: [],
    players_available_properties: [],
    reports_affiliates_types: [],
  }),
  team: () => ({}),
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const { t } = useI18n();
const route = useRoute();
//#endregion

//#region computed
const teamNavigation = computed(() => {
  const items = [
    {
      name: t('teams.sub_nav.users'),
      key: 'users',
      routeName: 'teams.teams-list.details.users',
    },
  ];

  if (props.team.type !== 'owners') {
    items.push({
      name: t('teams.sub_nav.permissions'),
      key: 'permissions',
      routeName: 'teams.teams-list.details.permissions',
    });
  }

  return items;
});
//#endregion
</script>

<template>
  <div class="team-info t:space-y-16">
    <div class="t:flex t:items-center t:gap-12">
      <UiTitle
        :font-size="24"
        class="t:font-medium"
      >
        {{ props.team.name }}
      </UiTitle>
      <UiAction
        v-if="props.team.id !== 'all'"
        icon="pen"
        color="var(--text-primary)"
        @click="emit('open-edit')"
      />
      <UiAction
        v-if="props.team.type !== 'owners'"
        icon="trash-alt"
        color="var(--text-negative)"
        @click="emit('open-delete')"
      />
    </div>
    <CardNavigation
      :list="teamNavigation"
      :params="{ id: props.team.id }"
      :query="route.query"
    />
    <RouterView v-slot="{ Component }">
      <component
        :is="Component"
        :team-info="props.team"
        :permissions="props.permissions"
        @refresh="emit('refresh')"
        @refresh-members="emit('refresh-members')"
        @refresh-users-list="emit('refresh-users-list')"
        @update-permissions="emit('update-permissions', $event)"
      />
    </RouterView>
  </div>
</template>

<style lang="scss">
.team-info {
  width: 66.6666%;
}
</style>
